import { Container } from '@/atoms/Container/Container';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/atoms/Dialog/Dialog';
import { Link } from '@/atoms/Link/Link';
import { List } from '@/atoms/List/List';
import { Typography } from '@/atoms/Typography/Typography';

const EMAIL_URL = 'mailto:info@synonym.to';
const EMAIL_REPORT_URL = 'mailto:report@synonym.to';
const COPYRIGHT_URL = '/copyright';
const PRIVACY_URL = '/settings/privacy-safety';

interface DialogTermsProps {
  trigger?: React.ReactNode;
}

export function DialogTerms({ trigger }: DialogTermsProps) {
  const baseCSS = 'text-muted-foreground text-base font-normal';
  const baseCSSItalic = 'text-muted-foreground text-base font-normal italic';

  const defaultTrigger = (
    <Typography as="span" size="sm" className="cursor-pointer font-medium text-brand">
      Terms of Service
    </Typography>
  );

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-xl" hiddenTitle="Terms of Service">
        <DialogHeader className="pr-6">
          <DialogTitle>Terms of Service</DialogTitle>
        </DialogHeader>
        <Container className="h-[320px] overflow-y-auto pr-4">
          <Container className="flex-col gap-4">
            {/** Section 1 */}
            <Typography className={baseCSS}>
              TERMS AND CONDITIONS
              <br />
              Effective Date: January 29, 2026
            </Typography>
            <Typography className={baseCSS}>
              Thank you for using the Pubky platform and the products, services and features we make available to you as
              part of the platform, including the Pubky App (collectively, the &quot;Platform&quot;). The terms and
              conditions set forth below (as updated and amended from time to time, and collectively with the Privacy
              Policy and any other materials explicitly incorporated by us, these &quot;Terms&quot;) govern your access
              to and use of the Platform.
            </Typography>
            <Typography className={baseCSS}>
              <strong>
                PLEASE REVIEW THE ARBITRATION PROVISION SET FORTH BELOW CAREFULLY, AS IT WILL REQUIRE ALL PERSONS TO
                RESOLVE DISPUTES ON AN INDIVIDUAL BASIS THROUGH FINAL AND BINDING ARBITRATION AND TO WAIVE ANY RIGHT TO
                PROCEED AS A REPRESENTATIVE OR CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE PROCEEDING. BY USING THE
                PLATFORM, YOU EXPRESSLY ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTAND ALL OF THE TERMS OF THIS PROVISION
                AND HAVE TAKEN TIME TO CONSIDER THE CONSEQUENCES OF THIS IMPORTANT DECISION.
              </strong>
            </Typography>
            <Typography className={baseCSS}>
              <strong>
                THESE TERMS FORM A LEGALLY BINDING AGREEMENT BETWEEN YOU AND SYNONYM (AS DEFINED BELOW). BY ACCESSING,
                DOWNLOADING OR USING THE PLATFORM, YOU CONFIRM THAT YOU ACCEPT THESE TERMS AND AGREE TO COMPLY WITH
                THEM. IF YOU DO NOT AGREE TO BE BOUND BY THESE TERMS OR OTHER REFERENCED DOCUMENTATION, YOU MUST CEASE
                TO ACCESS OR USE THE PLATFORM. IF YOU ARE USING THE PLATFORM ON BEHALF OF ANOTHER PERSON OR ENTITY, YOU
                REPRESENT THAT YOU HAVE THE AUTHORITY TO ACT ON BEHALF OF SUCH PERSON OR ENTITY, AND THAT SUCH PERSON OR
                ENTITY ACCEPTS THESE TERMS.
              </strong>
            </Typography>

            {/** Section 2 */}
            <Typography className={baseCSS}>
              INTRODUCTION
              <br />
              In order to assist your understanding of these Terms, we have included, in italicised text, an
              introductory paragraph to each section. These introductions should not be viewed as a substitute for
              reading the full text and are qualified by the text in full. If you have any doubt over the meaning of
              these Terms, please contact us at <Link href={EMAIL_URL}>info@synonym.to</Link> before you use the
              Platform. Any decision to utilise the Platform should be based on consideration of these Terms as a whole.
            </Typography>
            <Typography className={baseCSSItalic}>This section introduces Synonym and these Terms.</Typography>
            <Typography className={baseCSS}>
              These Terms constitute the entire agreement and understanding with respect to the access or use of any or
              all of the Platform and any manner of accessing the Platform, between you (referred to as “
              <strong>you</strong>”, “<strong>your</strong>”, or “<strong>User</strong>”) and Synonym Software Ltd., a
              company operating under the laws of the Republic of El Salvador, located at 87 Avenida Norte, Calle El
              Mirador, Edificio Torre Futura, Oficina 06, Nivel 11, Colonia Escalón, Del Municipio de San Salvador,
              Departamento de San Salvador, Código Postal 01101, República de El Salvador (referred to as “
              <strong>Synonym</strong>”, “<strong>we</strong>”, “<strong>us</strong>”, or “<strong>our</strong>”) (each
              of you and Synonym being a “<strong>Party</strong>”, and collectively the “<strong>Parties</strong>”).
            </Typography>
            <Typography className={baseCSS}>
              Only Eligible Users as described below are permitted to access or use the Platform. Any Person that is not
              an Eligible User that utilises the Platform will be in breach of these Terms and may have their account
              closed immediately.
            </Typography>

            {/** Section 3 */}
            <Typography className={baseCSS}>ACCEPTANCE OF THESE TERMS AND CONDITIONS THROUGH USE</Typography>
            <Typography className={baseCSSItalic}>
              This section explains your acceptance of these Terms and our ability to update or revise these Terms.
            </Typography>
            <Typography className={baseCSS}>
              These Terms may be amended, changed, or updated by Synonym at any time and without prior notice to you.
              You should check back often on our website to confirm that your copy and understanding of these Terms is
              current and correct. Your non-termination or continued access or use of the Platform after the effective
              date of any amendments, changes, or updates constitutes your acceptance of these Terms, as modified by
              such amendments, changes, or updates. Your only recourse in the case of your unwillingness to continue to
              be bound by these Terms is to stop using the Platform altogether and to request the closure of your
              account.
            </Typography>
            <Typography className={baseCSS}>
              Access or use of the Platform is void where such access or use is prohibited by, would constitute a
              violation of, or would be subject to penalties under applicable Laws, and shall not be the basis for the
              assertion or recognition of any interest, right, remedy, power, or privilege.
            </Typography>

            {/** Section 4 */}
            <Typography className={baseCSS}>REQUIREMENTS TO USE THE PLATFORM</Typography>
            <Typography className={baseCSSItalic}>
              This section explains the requirements and restrictions that Users are subject to when using the Platform.
            </Typography>
            <Typography className={baseCSS}>
              <strong>User Identity Requirements:</strong>
              <br />
              You acknowledge and agree that in order to use the Platform:
            </Typography>
            <List>
              <li>You must be at least eighteen (18) years old;</li>
              <li>
                We must not have previously disabled your account for violation of Law or any of our policies referenced
                herein;
              </li>
              <li>You must not be a convicted sex offender;</li>
              <li>
                If you are under eighteen (18) years old, you represent that you have your parent or legal guardian’s
                permission to use the Platform and will have your parent or legal guardian read these Terms with you;
                provided, that if you are a parent or legal guardian of a User under the age of eighteen (18), by
                allowing your child to use the Platform, you are subject to these Terms and responsible for your child’s
                activity on the Platform; and
              </li>
              <li>
                If you are using the Platform on behalf of a company or organization, you represent that you have
                authority to act on behalf of that company or organization, and that such entity accepts these Terms.
              </li>
            </List>
            <Typography className={baseCSS}>
              <strong>Eligible Users:</strong>
              <br />
              Users of the Platform shall be “Eligible Users” where: they comply with the “User Identity Requirements”
              listed above and they do not utilise the Platform to facilitate any Prohibited Uses as described below.
              The access or use of any of the Platform by any Person other than an Eligible User is void and shall not
              be the basis for the assertion or recognition of any interest, right, remedy, power, or privilege.
            </Typography>

            {/** Section 5 */}
            <Typography className={baseCSS}>
              <strong>License to Synonym:</strong>
              <br />
              When you share, post or upload Content as described below that is covered by intellectual property rights
              (such as photos or videos) on or in connection with the Platform, you hereby grant to us a non-exclusive,
              royalty-free, transferable, sublicensable, worldwide license to host, use, distribute, modify, run, copy,
              publicly perform or display, translate and create derivative works of your Content (consistent with your
              settings). This license will end when your Content is deleted from our systems. You understand and agree,
              however, that we may retain, but not display, distribute, or perform, server copies of your Content that
              has been removed or deleted.
            </Typography>
            <Typography className={baseCSS}>
              <strong>License to Users:</strong>
              <br />
              You also grant each other user of the Platform a worldwide, non-exclusive, royalty-free license to access
              your Content through the Platform, and to use that Content, including to reproduce, distribute, prepare
              derivative works, display, and perform it, only as enabled by a feature of the Platform (such as video
              playback or embeds). For clarity, this license does not grant any rights or permissions for a User to make
              use of your Content independent of the Platform.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Removal of Content:</strong>
              <br />
              You may remove your Content from the Platform at any time. You also have the option to make a copy of your
              Content before removing it. You must remove your Content if you no longer have the rights required by
              these Terms.
            </Typography>
            <Typography className={baseCSS}>
              You can report suspected illegal Content or Content which otherwise breaches these Terms. To contact us,
              please follow the “Complaint Procedure” outlined below.
            </Typography>

            {/** Section 6 */}
            <Typography className={baseCSS}>CRYPTOGRAPHIC KEYS AND ACCOUNT RECOVERY</Typography>
            <Typography className={baseCSS}>
              This section explains your responsibilities regarding the cryptographic keys that secure your account.
            </Typography>
            <Typography className={baseCSS}>
              Your Pubky account is secured by cryptographic keys that only you control. During the signup process, you
              will be provided with recovery options including:
            </Typography>
            <Typography className={baseCSS}>
              <strong>Recovery Phrase:</strong> A 12-word phrase that can restore your account.
              <br />
              <strong>Encrypted Backup File:</strong> A password-protected .pkarr file.
              <br />
              <strong>QR Code:</strong> A scannable backup option.
            </Typography>
            <Typography className={baseCSS}>
              <strong>
                IMPORTANT: We do not store your private keys or recovery phrase. If you lose all means of recovery, your
                account cannot be recovered by Synonym or any third party. You alone are responsible for safeguarding
                your recovery information, keeping your recovery phrase and backup files secure and confidential, not
                sharing your private keys or recovery phase with any third party, and maintaining backups of your
                recovery materials listed above.
              </strong>
            </Typography>
            <Typography className={baseCSS}>
              If you believe your account credentials have been compromised, you should immediately create a new account
              and transfer your content as appropriate.
            </Typography>

            {/** Section 7 */}
            <Typography className={baseCSS}>HUMAN VERIFICATION</Typography>
            <Typography className={baseCSS}>
              This section explains the verification process required to use the Platform.
            </Typography>
            <Typography className={baseCSS}>
              To prevent spam and ensure the quality of the Platform, you must complete human verification during
              account creation. We offer the following verification methods:
            </Typography>
            <List>
              <li>
                <strong>SMS Verification</strong>: You may verify your account by receiving a one-time code via SMS to
                your phone number. Standard message and data rates may apply. SMS verification may not be available in
                all regions.
              </li>
              <li>
                <strong>Lightning Payment Verification</strong>: You may verify your account by making a small payment
                via the Lightning Network. Lightning payment verification may not be available in all regions due to
                applicable Laws and regulatory requirements.
              </li>
              <li>
                <strong>Invite Code Verification</strong>: You may verify your account using an invite code provided by
                another user or by Synonym.
              </li>
            </List>

            {/** Section 8 */}
            <Typography className={baseCSS}>REGIONAL AVAILABILITY</Typography>
            <Typography className={baseCSS}>
              This section explains geographic restrictions on Platform features.
            </Typography>
            <Typography className={baseCSS}>
              Certain features and verification methods may not be available in all jurisdictions due to legal,
              regulatory, or operational requirements. We reserve the right to restrict access to specific features
              based on your location.
            </Typography>
            <Typography className={baseCSS}>
              The Platform is not available to users in Prohibited Jurisdictions as defined below.
            </Typography>

            {/** Section 9 */}
            <Typography className={baseCSS}>COMMUNITY GUIDELINES</Typography>
            <Typography className={baseCSSItalic}>This section explains the Synonym Community Guidelines.</Typography>
            <Typography className={baseCSS}>
              Synonym enables creators of Content to submit Content to the Platform. To allow all users of the Platform
              to safely participate in public conversation, you agree to comply with all applicable Laws when submitting
              Content to the Platform. In addition, you agree that under no circumstances shall the following be
              submitted to the Platform:
            </Typography>
            <List>
              <li>
                Content which includes, in whole or in part, audio, video, text, or images that are subject to copyright
                by another person unless such inclusion is subject to applicable Laws concerning fair use or is
                otherwise expressly authorized by the copyright holder;
              </li>
              <li>
                Content that is subject to any third-party contractual provisions or limitations, including license
                restrictions;
              </li>
              <li>
                Content which displays the trademarks of another person without that person&apos;s authorization,
                subject to applicable Laws concerning fair use;
              </li>
              <li>Content that is pornographic, obscene, or of an adult or sexual nature;</li>
              <li>Content that is offensive, including harassment and discrimination;</li>
              <li>
                Content that:
                <List>
                  <li>Promotes, supports, or incites violence or unlawful acts;</li>
                  <li>
                    Promotes, supports, or incites individuals and/or groups which engage in violence, hateful or
                    unlawful acts, such as extremist or criminal organisations; and/or
                  </li>
                  <li>
                    Promotes or supports entities and/or persons designated by governments of the United States, the
                    Republic of El Salvador, or the United Nations as terrorists or terrorist organizations.
                  </li>
                </List>
              </li>
              <li>
                Content that exploits children under the age of eighteen (18) or posts or discloses any personally
                identifying information about any person at any age, including personally identifying information about
                children under the age of eighteen (18);
              </li>
              <li>
                Content that displays, promotes, or provides instructional information about illegal activities or harm
                to any group, individual or animal;
              </li>
              <li>
                Content that infringes or encroaches on the rights of others, including infringement of privacy,
                publicity rights, and harm to reputation; and
              </li>
              <li>
                Any other Content or material that we in our sole, unfettered, and arbitrary discretion, determine
                undesirable on the Platform.
              </li>
            </List>
            <Typography className={baseCSS}>
              We have the sole discretion to decide whether Content or material is permitted on the Platform and any
              materials submitted to the Platform may be, but are not necessarily, examined by us before they are made
              available on the Platform. You acknowledge that we have the absolute right (but not the obligation) to
              prohibit, refuse, delete, move and edit Content for any reason, in any manner, at any time, without notice
              to you except as otherwise required by applicable Law. We can also suspend or disable your access to the
              Platform for certain conduct as described in these Terms.
            </Typography>
            <Typography className={baseCSS}>
              You should also know that in using the Platform, you may be exposed to materials which you consider to be
              offensive or inappropriate and you assume the risk and sole responsibility for your exposure to any such
              Content or material.
            </Typography>

            {/** Section 10 */}
            <Typography className={baseCSS}>COMPLAINT PROCEDURE</Typography>
            <Typography className={baseCSSItalic}>
              This Section explains how to report a violation of the Terms.
            </Typography>
            <Typography className={baseCSS}>
              To report any violation of our Terms, please email
              <Link href={EMAIL_REPORT_URL}> report@synonym.to</Link>. Please use the word “<strong>Complaint</strong>”
              in the subject line, and include the following information in your email:
            </Typography>
            <List>
              <li>Your name and, if you are a registered user of the Platform, your user ID;</li>
              <li>The basis of your complaint (provide as much detail as possible);</li>
              <li>
                If your complaint concerns the activities of other Users/visitors on the Platform, identify the specific
                type of inappropriate or offensive behaviour engaged in and, insofar as possible, the identity of the
                offending person; and
              </li>
              <li>
                If your complaint concerns particular Content, please provide the URL for the Content that is the
                subject of your complaint, timestamps within the video where the alleged violative Content appears, and
                the reason for your Complaint.
              </li>
            </List>
            <Typography className={baseCSS}>
              If, in our determination, your complaint is a valid one, we will take appropriate actions in our sole
              discretion. If you have been restricted due to a violation of the Community Guidelines, you must not use
              another channel to circumvent these restrictions. Violation of this prohibition is a material breach of
              these Terms, and Synonym reserves the right to terminate your account or access to all of part of the
              Platform in its sole discretion.
            </Typography>

            {/** Section 11 */}
            <Typography className={baseCSS}>COMPLIANCE WITH APPLICABLE LAW</Typography>
            <Typography className={baseCSSItalic}>
              This section requires that you comply with all applicable Laws in using the Platform.
            </Typography>
            <Typography className={baseCSS}>
              You agree to comply with all applicable Laws with respect to your use of the Platform, including but not
              limited to sanctions, anti-money laundering, export control, privacy, and anti-terrorism Laws. Without
              limiting the foregoing, you acknowledge and agree that Synonym makes no representation that materials
              accessible via the Platform are appropriate or available for use in any particular locations, and
              accessing them from territories where their contents are illegal is prohibited. Those who choose to access
              the Platform from any location do so on their own initiative, at their own risk, and are responsible for
              compliance with all applicable Laws. If you use the Platform in a jurisdiction that prohibits or restricts
              such use, your use will be subject to any other provision of these Terms, and Synonym shall not have any
              liability with respect to such use.
            </Typography>
            <Typography className={baseCSS}>
              You further agree to comply with all state, provincial and/or federal regulations with respect to the
              Content you upload to the Platform, including those regulations related to accessibility (e.g. closed
              captioning of Content where required by law).
            </Typography>

            {/** Section 12 */}
            <Typography className={baseCSS}>PRIVACY POLICY</Typography>
            <Typography className={baseCSSItalic}>This section provides a link to our Privacy Policy.</Typography>
            <Typography className={baseCSS}>
              Our <Link href={PRIVACY_URL}>Privacy Policy</Link> explains how we treat your personal data and protect
              your privacy information when you use the Platform. The Privacy Policy also explains the many ways you can
              control your information.
            </Typography>

            {/** Section 13 */}
            <Typography className={baseCSS}>INTELLECTUAL PROPERTY</Typography>
            <Typography className={baseCSSItalic}>
              This section lays out the requirements to abide by all Laws applicable to copyrighted material,
              intellectual property or contractual rights or interests while using the Platform.
            </Typography>
            <Typography className={baseCSS}>
              You affirm, represent, and warrant that you have the legal right, including copyright, to submit your
              Content to the Platform.
            </Typography>
            <Typography className={baseCSS}>
              You further represent and warrant that any Content submitted does not contain third-party copyrighted
              material, or material that is subject to other third-party proprietary, intellectual property or
              contractual rights or interests, unless you have express written permission from the rights holder of the
              relevant material or the usage constitutes fair use under applicable Law. In the event you claim to have
              been granted such rights from a third-party, you agree to furnish evidence of same in writing forthwith
              upon request by Synonym. You are required to obtain such written permission before submitting such Content
              to the Platform. Such written permission must not be limited in any way and shall remain in effect for the
              duration that the Content remains on the Platform.
            </Typography>
            <Typography className={baseCSS}>
              Any infringement or other violation of a third-party&apos;s rights with respect to the Content submitted
              by you to the Platform and which has been identified as such by Synonym or by a third-party, may
              immediately and without notice to you result in the removal of such Content. You hereby agree to defend,
              indemnify and hold harmless Synonym and its Associates, as to any allegations, demands, claims,
              investigations or disputes arising from your submission of Content to Synonym and/or monetization of your
              Content, insofar as it relates to any infringement or violation of the intellectual property rights,
              proprietary rights, contractual rights, or other rights of a third-party.
            </Typography>
            <Typography className={baseCSS}>
              If you believe your copyright has been infringed on the Platform, please send us a{' '}
              <Link href={COPYRIGHT_URL}>Takedown Notice</Link>. We respond to notices of alleged copyright infringement
              according to the process laid out in
              <Link href={COPYRIGHT_URL}> Takedown Notice</Link>.
            </Typography>
            <Typography className={baseCSS}>
              If you believe your Content has been wrongfully removed from the Platform, please send us a{' '}
              <Link href={COPYRIGHT_URL}>Counter Notification</Link>. Please note that any person who knowingly
              materially misrepresents that material or activity was removed or disabled by mistake or misidentification
              may be subject to liability.
            </Typography>
            <Typography className={baseCSS}>
              Synonym policies provide for the termination, in appropriate circumstances, of repeat infringers’ access
              to the Platform.
            </Typography>

            {/** Section 14 */}
            <Typography className={baseCSS}>THIRD-PARTY MATERIALS</Typography>
            <Typography className={baseCSSItalic}>
              This section explains the risks of websites and other services provided by parties other than Synonym.
            </Typography>
            <Typography className={baseCSS}>
              The Platform may provide Third-Party Materials, and thereby produce third-party advertisements and/or
              third-party search results. The Platform and/or Content may otherwise link to Third-Party Materials.
              Synonym is not responsible for the content, privacy settings, policies and/or procedures of Third-Party
              Materials. Products and services comprising Third-Party Materials are the sole responsibility of each
              individual author, vendor or operator of the Third-Party Materials. The inclusion and/or availability of
              links to Third-Party Materials does not imply endorsement of such Third-Party Materials or any content,
              information, material or products, nor does it imply any association with the authors, vendors or
              operators of such Third-Party Materials. Third-Party Materials may contain information or content that
              some people may find inappropriate or offensive. You will need to make your own independent judgement
              regarding your interaction with Third-Party Materials. Synonym makes no representations or warranties
              whatsoever concerning:
            </Typography>
            <List>
              <li>
                the information, software or other material appearing on, or accessible through, any Third-Party
                Materials, including any advertisement for products or services;
              </li>
              <li>
                the performance or operation of any Third-Party Materials, including any transactions initiated or
                conducted, any taxes associated therewith and any use by third-parties of User personal information;
              </li>
              <li>
                any products or services advertised or sold on or through any Third-Party Materials, including the
                quality, safety and legality of such products or services or the sale thereof;
              </li>
              <li>
                the sellers of any products or services advertised or sold on or through any Third-Party Materials; or
              </li>
              <li>
                the accuracy, copyright compliance, legality, decency, or any other aspect of the content of any
                Third-Party Material.
              </li>
            </List>
            <Typography className={baseCSS}>
              If you decide to access any Third-Party Materials, you do so entirely at your own risk. If you are
              accessing Third-Party Materials through the Platform, you are advised to read any terms of use and privacy
              policies of such Third-Party Materials before you use or access them.
            </Typography>
            <Typography className={baseCSS}>
              You understand, acknowledge, and agree that where the Platform is licensed for use by a third-party, such
              third-party may be using the Platform for certain purposes or to display certain content, that is not
              content owned, controlled, distributed, authorized or endorsed by Synonym.
            </Typography>
            <Typography className={baseCSS}>
              We do not guarantee that the Platform will be secure or free from bugs, viruses or other harmful
              components. You are responsible for configuring your information technology, computer programmes and
              platform to access and utilise the Platform. You should use your own antivirus and anti-malware protection
              software.
            </Typography>

            {/** Section 15 */}
            <Typography className={baseCSS}>PUBLISHING AND DISTRIBUTION OF CONTENT VIA THE PLATFORM</Typography>
            <Typography className={baseCSSItalic}>
              This section explains that while the Platform may choose to publish user content across different media
              and formats at its discretion, it is not obligated to do so and reserves the right to reject any submitted
              content.
            </Typography>
            <Typography className={baseCSS}>
              We may, but are not obliged to, have your Content published and displayed through various media channels
              and in various formats via the Platform, in our sole commercial discretion. We reserve the right to
              decline or reject any Content submitted to the Platform in our sole discretion.
            </Typography>

            {/** Section 16 */}
            <Typography className={baseCSS}>PROHIBITED USES</Typography>
            <Typography className={baseCSSItalic}>
              This section sets out what you must not use the Platform for, and the actions we might take if you breach
              these restrictions.
            </Typography>
            <Typography className={baseCSS}>
              You agree not to engage in any of the following prohibited activities: (i) using any automated system,
              including “robots,” “spiders,” “offline readers,” etc., to access the Platform in a manner that sends more
              request messages to the Synonym servers than a human can reasonably produce in the same period of time by
              using a conventional on-line web browser (except that Synonym grants the operators of public search
              engines revocable permission to use spiders to copy publicly available materials from the Platform for the
              sole purpose of and solely to the extent necessary for creating publicly available searchable indices of
              the materials, but not caches or archives of such materials); (ii) transmitting spam, chain letters, or
              other unsolicited email; (iii) attempting to interfere with, compromise the system integrity or security
              or decipher any transmissions to or from the servers running the Platform; (iv) taking any action that
              imposes, or may impose at our sole discretion an unreasonable or disproportionately large load on our
              infrastructure; (v) uploading invalid data, viruses, worms, or other software agents through the Platform;
              (vi) collecting or harvesting any personally identifiable information, including account names, from the
              Platform; (vii) using the Platform for any commercial solicitation purposes in violation with all
              applicable Laws thereto; (viii) interfering with the proper working of the Platform; (ix) bypassing any of
              the security or privacy measures of the Platform; (x) using the Platform if any applicable Laws prohibit,
              penalize, sanction, or otherwise expose Synonym or any of its affiliates, officers, directors, employees,
              agents, or contractors to liability in connection with your use of the Platform; (xi) using the Platform
              to facilitate, approve, evade, avoid, violate, attempt to violate, aid or abet the violation of, or
              otherwise circumvent any applicable Laws; (xii) using or accessing the Platform if you are not an Eligible
              User; or (xiii) using the Platform to evade or attempt to evade taxes under the Laws of any applicable
              jurisdiction.
            </Typography>
            <Typography className={baseCSS}>
              Any use as described in this section is a “<strong>Prohibited Use</strong>”. If Synonym determines or
              suspects that you have engaged in any Prohibited Use, Synonym may address such Prohibited Use through an
              appropriate sanction, in its sole and absolute discretion. Such sanction may include: (i) making a report
              to any government, law enforcement, or other authorities, without providing any notice of you about any
              such report; or (ii) immediately suspending or terminating your access to the Platform without notice to
              you. In addition, should your actions or inaction result in Loss being suffered by Synonym or any of its
              Associates, you shall pay an amount to Synonym or the Associate so as to render Synonym or the Associate
              whole, including the amount of taxes or penalties that might be imposed on Synonym or the Associate.
            </Typography>

            {/** Section 17 */}
            <Typography className={baseCSS}>NO REPRESENTATION BY SYNONYM</Typography>
            <Typography className={baseCSSItalic}>
              This section explains that we do not make any promises about the Platform and that we are providing the
              Platform on an “as is” basis. We cannot confirm that the Platform will suit your needs.
            </Typography>
            <Typography className={baseCSS}>
              Synonym makes no representations, warranties, covenants or guarantees to you of any kind and, to the
              extent permitted by applicable Laws, Synonym expressly disclaims all representations, warranties,
              covenants or guarantees, express, implied or statutory, with respect to the Platform. The Platform is
              offered strictly on an as-is, where-is basis and, without limiting the generality of the foregoing, is
              offered without any representation as to merchantability or fitness for any particular purpose. When you
              access the Platform or certain features or services comprising the Platform that are identified as “beta”
              or pre-release, you understand that such services are still in development, may have bugs or errors, may
              be feature incomplete, may materially change prior to a full commercial launch, or may never be released
              commercially.
            </Typography>
            <Typography className={baseCSS}>
              Without limiting the generality of the above paragraph, Synonym makes no representations, warranties,
              covenants or guarantees to you in respect of any Third-Party Materials.
            </Typography>

            {/** Section 18 */}
            <Typography className={baseCSS}>PUBKY IS A PROGRESSIVE WEB APPLICATION</Typography>
            <Typography className={baseCSSItalic}>
              This section explains certain requirements and restrictions because the Pubky App is a progressive web
              application.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Progressive Web Application</strong>:
              <br />
              To use the Pubky App you must have either a computer or a mobile device that is compatible with the Pubky
              App. Synonym does not warrant that the Pubky App will be compatible with your computer or mobile device.
              You may use mobile data in connection with the Pubky App used by mobile device and may incur additional
              charges from your wireless provider for these services. You agree that you are solely responsible for any
              such charges. You acknowledge that Synonym may from time to time issue upgraded versions of the Pubky App,
              and may automatically electronically upgrade the version of the Pubky App that you are using. You consent
              to such automatic upgrading on your computer or mobile device, and agree that the terms and conditions of
              these Terms will apply to all such upgrades.
            </Typography>

            {/** Section 19 */}
            <Typography className={baseCSS}>TAX</Typography>
            <Typography className={baseCSSItalic}>
              This section makes it clear that you are responsible for any taxes in relation to any transactions you
              carry out through the Platform.
            </Typography>
            <Typography className={baseCSS}>
              It is your sole responsibility to determine whether and to what extent taxes and tax reporting obligations
              may apply to you (including any goods and services tax) with respect to any transactions carried out
              through the Platform and you shall timely pay all such taxes and shall file all returns, reports, and
              disclosures required by applicable Law. You agree to indemnify and hold Synonym and its Associates
              harmless from and against any and all taxes (other than income or similar taxes on income earned by
              Synonym in providing the Platform) payable with respect to any transactions carried out through the
              Platform.
            </Typography>

            {/** Section 20 */}
            <Typography className={baseCSS}>RESPONSIBILITIES, LIMITATION OF LIABILITY AND INDEMNITY</Typography>
            <Typography className={baseCSSItalic}>
              In this section, we limit our liability to you and require you to release claims against us and indemnify
              us for any losses we incur as a result of a breach by you of these Terms.
            </Typography>
            <Typography className={baseCSS}>
              To the maximum extent permitted by applicable Law, you irrevocably agree and acknowledge that neither
              Synonym nor any of its Associates assumes any liability or responsibility for and neither Synonym nor any
              of its Associates shall have any liability or responsibility for any Losses directly or indirectly arising
              out of or related to the Platform.
            </Typography>
            <Typography className={baseCSS}>
              You hereby agree to release Synonym and its Associates from liability for any and all such Losses, and you
              shall indemnify and save and hold Synonym and its Associates harmless from and against all such Losses
              incurred by them as a result of your use of the Platform in breach of these Terms, in violation of
              applicable Laws, including but not limited to sanctions, anti-money laundering, export control, privacy,
              and anti-terrorism Laws, for any Content or for any stolen, lost, or unauthorized use of any account
              credentials, data or other information. To the maximum extent permitted by applicable Law, the foregoing
              indemnity and limitations of liability and releases shall apply whether the alleged liability or Losses
              are based on contract, negligence, tort, unjust enrichment, strict liability, violation of law or
              regulation, or any other basis, even if Synonym or any of its Associates have been advised of or should
              have known of the possibility of such Losses and damages, and without regard to the success or
              effectiveness of any other remedies.
            </Typography>
            <Typography className={baseCSS}>
              To the fullest extent permissible by Law, the maximum aggregate monetary liability of Synonym under these
              Terms shall in no event exceed the fees paid by you to Synonym (if any) in respect of the Platform in
              relation to which the liability has arisen.
            </Typography>

            {/** Section 21 */}
            <Typography className={baseCSS}>FORCE MAJEURE</Typography>
            <Typography className={baseCSSItalic}>
              This section explains that we cannot be held responsible for things outside of our control.
            </Typography>
            <Typography className={baseCSS}>
              Synonym is not responsible for Losses caused by delay or failure of Synonym or the Platform, including
              when the delay or failure is due to fires; strikes; floods; power outages or failures; pandemics and
              public health events; acts of God or the state’s enemies; disease pandemics; government acts; any and all
              market movements, shifts, or volatility; computer, server, protocol or internet malfunctions; security
              breaches or cyberattacks; criminal acts; delays or defaults caused by common carriers; acts or omissions
              of other Persons; or, any other delays, defaults, failures or interruptions that cannot reasonably be
              foreseen or provided against by Synonym.
            </Typography>

            {/** Section 22 */}
            <Typography className={baseCSS}>MANDATORY RESOLUTION OF DISPUTES THROUGH ARBITRATION</Typography>
            <Typography className={baseCSSItalic}>
              This Section requires that most disputes relating to the Platform be resolved through individual
              arbitration.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Covered Claims:</strong>
              <br />
              Except for excluded claims described in the paragraph below, Synonym and you each agree that any dispute,
              claim or controversy arising out of or relating to (i) these Terms or the existence, breach, termination,
              enforcement, interpretation or validity thereof, (ii) your Content or (iii) your use of Pubky at any time,
              will be subject to and finally resolved by confidential, binding arbitration on an individual basis and
              not in a class, representative or consolidated action or proceeding. If you are a person subject to the
              jurisdiction of the United States of America, the interpretation and enforceability of this arbitration
              provision will be governed by the Federal Arbitration Act, 9 U.S.C. §§ 1 et seq. Arbitration will be
              conducted through the use of videoconferencing technology (unless both parties agree that an in-person
              hearing is appropriate given the nature of the dispute) before a single arbitrator in accordance with the
              LCIA Rules. The sole arbitrator must be a legal practitioner in London, England with at least fifteen (15)
              years of experience in commercial disputes, that holds a current practising certificate. If an arbitrator
              cannot be jointly appointed by the arbitration parties within thirty (30) days of the commencement of the
              arbitration, an arbitrator meeting the above qualifications will be selected under the Arbitration Rules
              of the London Court of International Arbitration (LCIA). Judgement upon the award rendered by the
              arbitrator may be entered by any court having jurisdiction thereof. If the arbitral parties do not
              promptly agree on the seat of arbitration if an in-person hearing is selected, the seat will be London,
              England. The language of the arbitral proceedings will be English. No discovery shall be conducted except
              by agreement of the parties or after approval by the arbitrator, who shall attempt to minimize the burden
              of discovery. The arbitrator may award any relief that a court of competent jurisdiction could award,
              including attorneys’ fees when authorized by laws, and the arbitral decision may be enforced in court. For
              claims less than U.S.$15,000, Synonym will reimburse you for all initiating filing fees in the event that
              the claim is successful. The prevailing party, as determined by the arbitrator, will be entitled to its
              costs of the arbitration (including the arbitrator’s fees) and its reasonable attorney’s fees and costs.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Excluded Claims:</strong>
              <br />
              The following claims and causes of action will be excluded from arbitration as described in the paragraph
              above: causes of action or claims in which either Party seeks injunctive or other equitable relief for the
              alleged unlawful use of its intellectual property or its confidential information or private data. The
              Parties shall be at liberty to pursue claims or causes of actions excluded from arbitration through any
              court of competent jurisdiction.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Delegation:</strong>
              <br />
              The arbitrator will have the power to hear and determine challenges to its jurisdiction, including any
              objections with respect to the formation, existence, scope, enforceability or validity of the arbitration
              agreement. This authority extends to jurisdictional challenges with respect to both the subject matter of
              the dispute and the parties to the arbitration. Further, the arbitrator will have the power to determine
              the existence, validity, or scope of the contract of which an arbitration clause forms a part. For the
              purposes of challenges to the jurisdiction of the arbitrator, each clause in this section will be
              considered as separable from any contract of which it forms a part. Any challenges to the jurisdiction of
              the arbitrator, except challenges based on the award itself, will be made not later than the notice of
              defense or, with respect to a counterclaim, the reply to the counterclaim; provided, however, that if a
              claim or counterclaim is later added or amended such a challenge may be made not later than the response
              to such claim or counterclaim as provided under LCIA Rules.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Class Action Waiver:</strong>
              <br />
              You and Synonym expressly intend and agree that: (i) class action and representative action procedures are
              hereby waived and will not be asserted, nor will they apply, in any arbitration pursuant to these Terms;
              (ii) neither you nor Synonym will assert class action or representative action claims against the other in
              arbitration or otherwise; (iii) each of you and Synonym will only submit their own, individual claims in
              arbitration and will not seek to represent the interests of any other person, or consolidate claims with
              any other person; (iv) nothing in these Terms will be interpreted as your or Synonym’ intent to arbitrate
              claims on a class or representative basis; and (v) any relief awarded to any one User cannot and may not
              affect any other User. No adjudicator may consolidate or join more than one Person’s or Party’s claims and
              may not otherwise preside over any form of a consolidated, representative, or class proceeding.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Confidentiality:</strong>
              <br />
              You and Synonym and any other arbitration parties will maintain the confidential nature of the arbitration
              proceeding and any award, including the hearing, except as may be necessary to prepare for or conduct the
              arbitration hearing on the merits, or except as may be necessary in connection with a court application
              for a preliminary remedy, a judicial challenge to an award or its enforcement, or unless otherwise
              required by Law or judicial decision.
            </Typography>

            {/** Section 23 */}
            <Typography className={baseCSS}>
              <strong>JURY TRIAL WAIVER:</strong>
              <br />
              <strong>
                TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, THE PARTIES HEREBY IRREVOCABLY AND UNCONDITIONALLY
                WAIVE ALL RIGHT TO TRIAL BY JURY IN ANY LEGAL ACTION OR PROCEEDING OF ANY KIND WHATSOEVER ARISING OUT OF
                OR RELATING TO THESE TERMS OR ANY BREACH THEREOF, ANY USE OR ATTEMPTED USE OF THE PLATFORM BY YOU,
                AND/OR ANY OTHER MATTER INVOLVING THE USER AND SYNONYM.
              </strong>
            </Typography>

            {/** Section 24 */}
            <Typography className={baseCSS}>DEFINITIONS</Typography>
            <Typography className={baseCSSItalic}>
              In this section, we define certain terms used throughout these Terms. If a word is capitalised in these
              Terms, please refer to this section for its intended meaning.
            </Typography>
            <Typography className={baseCSS}>
              In these Terms, the following words have the following meanings, unless otherwise indicated:
            </Typography>
            <List>
              <li>
                “<strong>Associates</strong>” means Synonym, its subsidiaries and affiliates and their respective
                officers, directors, agents, co-branders and other partners, contractors, and employees;
              </li>
              <li>
                “<strong>Community Guidelines</strong>” means the Pubky Community Guidelines;
              </li>
              <li>
                “<strong>Content</strong>” means user-generated text, images, videos and other content provided on the
                Platform;
              </li>
              <li>
                “<strong>Law</strong>” means all laws, statutes, orders, regulations, rules, treaties, and/or official
                obligations or requirements enacted, promulgated, issued, ratified, enforced, or administered by any
                Government that apply to you, Synonym or the Platform;
              </li>
              <li>
                “<strong>LCIA Rules</strong>” means LCIA Arbitration Rules 2020;
              </li>
              <li>
                “<strong>Losses</strong>” means, collectively, any claim, application, loss, injury, delay, accident,
                cost, business interruption costs, or any other expenses (including attorneys’ fees or the costs of any
                claim or suit), including any incidental, direct, indirect, general, special, punitive, exemplary, or
                consequential damages, loss of goodwill or business profits, work stoppage, data loss, computer failure
                or malfunction, or any and all other commercial losses;
              </li>
              <li>
                “<strong>Person</strong>” includes an individual, association, partnership, corporation, company, other
                body corporate, trust, estate, and any form of organization, group, or entity (whether or not having
                separate legal personality);
              </li>
              <li>
                “<strong>Privacy Policy</strong>” means the Pubky
                <Link href={PRIVACY_URL}> Privacy Policy</Link> located at:
                <Link href={PRIVACY_URL}> https://pubky.app/privacy</Link>.
              </li>
              <li>
                “<strong>Prohibited Jurisdiction</strong>” means any of: Cuba, Democratic People’s Republic of Korea
                (North Korea), Iran, Syria, Crimea (a region of Ukraine annexed by the Russian Federation), the
                self-proclaimed Donetsk People’s Republic (a region of Ukraine) and the self-proclaimed Luhansk People’s
                Republic (a region of Ukraine);
              </li>
              <li>
                “<strong>Pubky App</strong>” means the progressive web application software named Pubky as provided by
                Synonym;
              </li>
              <li>
                “<strong>Third-Party Material(s)</strong>” means sites on the Internet, software application, content
                other than Content submitted by Users, data and other information that are not authored, operated or
                controlled by Synonym; and
              </li>
              <li>
                “<strong>Users</strong>” means all users and others who access the Platform.
              </li>
            </List>

            {/** Section 25 */}
            <Typography className={baseCSS}>MISCELLANEOUS</Typography>
            <Typography className={baseCSSItalic}>
              This section contains provisions relating to which law governs these Terms, our relationship and whether
              we can transfer the rights of these Terms, among other things.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Governing law:</strong>
              <br />
              These Terms shall be governed by and construed and enforced in accordance with the Laws of the England and
              Wales and shall be interpreted in all respects as an English law contract. Any transaction, dispute,
              controversy, claim or action arising from or related to your access to the Platform or these Terms shall
              be governed by the Laws of England and Wales, exclusive of choice-of-law principles.
            </Typography>
            <Typography className={baseCSS}>
              <strong>No Waiver; Available Remedies:</strong>
              <br />
              Any failure by Synonym to exercise any of its rights, powers, or remedies under these Terms, or any delay
              by Synonym in doing so, does not constitute a waiver of any such right, power, or remedy. The single or
              partial exercise of any right, power, or remedy by Synonym does not prevent either from exercising any
              other rights, powers, or remedies. The remedies of Synonym are cumulative with and not exclusive of any
              other remedy conferred by the provisions of these Terms, or by law or equity. You agree that the remedies
              to which Synonym is entitled include (i) injunctions to prevent breaches of these Terms and to enforce
              specifically the terms and provisions hereof, and you waive the requirement of any posting of a bond in
              connection with such remedies, (ii) the right to recover the amount of any Losses by set off against any
              amounts that Synonym would otherwise be obligated to pay to you, and (iii) the right to seize and recover
              against any of your assets, or your interests therein, that are held by Synonym or any of its Associates.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Assignment and Third-Party Rights:</strong>
              <br />
              These Terms, and any of the rights, duties, and obligations contained or incorporated herein, are not
              assignable by you without prior written consent of Synonym and any attempt by you to assign these Terms
              without Synonym’s written consent is void. These Terms, and any of the rights, duties, and obligations
              contained herein, are freely assignable by Synonym, in whole or in part, without notice or your consent
              (for clarity, this assignment right includes the right for Synonym to assign any claim, in whole or in
              part, arising hereunder). Any attempt by you to assign these Terms without written consent is void.
              Subject to the foregoing, these Terms, and any of the rights, duties, and obligations contained or
              incorporated herein, shall be binding upon and inure to the benefit of the heirs, executors,
              administrators, personal or legal representatives, successors and assigns of you and of Synonym. None of
              the provisions of these Terms, or any of the rights, duties, and obligations contained or incorporated
              herein, are for the benefit of or enforceable by any creditors of you or Synonym or any other persons,
              except: (i) such as inure to a successor or assign in accordance herewith; and (ii) that the Associates of
              Synonym are intended third party beneficiaries of the rights and privileges expressly stated to apply to
              the Associates hereunder and shall be entitled to enforce such rights and privileges (including those
              rights and privileges set out in the paragraph titled “Responsibilities, Limitation of Liability and
              Indemnity”) as if a direct party to these Terms. No consent of any Person is required for any modification
              or amendment to these Terms.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Severability:</strong>
              <br />
              If any provision of these Terms or part thereof, as amended from time to time, is determined to be
              invalid, void, or unenforceable, in whole or in part, by any court of competent jurisdiction, such
              invalidity, voidness, or unenforceability attaches only to such provision to the extent of its illegality,
              unenforceability, invalidity, or voidness, as may be, and everything else in these Terms continues in full
              force and effect.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Electronic Communications and Acceptance:</strong>
              <br />
              You agree and consent to receive electronically all communications, agreements, documents, receipts,
              notices and disclosures that Synonym may provide in connection with these Terms through publication on any
              part of the Platform or to an e-mail address on file that you have previously provided to Synonym. Such
              notices shall be deemed effective and received by you on the date on which the notice is published on any
              part of the Platform or on which the e-mail is sent to such e-mail address. These Terms may be accepted
              electronically, and it is the intention of the Parties that such acceptance shall be deemed to be as valid
              as an original signature being applied to these Terms.
            </Typography>
            <Typography className={baseCSS}>
              <strong>Termination and No Liability for Termination:</strong>
              <br />
              We reserve the right, in our sole discretion, to suspend and/or terminate your access to the Platform for
              any reason, including if we determine that you (i) repeatedly provided manifestly illegal Content; (ii)
              repeatedly submitted manifestly unfounded notices or complaints; (iii) otherwise violated these Terms or
              applicable Law. This includes our right to terminate your ability to upload Content, post comments or use
              any function available via the Platform.
            </Typography>
            <Typography className={baseCSS}>
              In general, we will notify you at the latest from the date the action is taken and provide you with an
              opportunity to remedy the relevant breach or breaches. However, advanced notification and/or opportunity
              to remedy will not be provided if the seriousness of the breach requires immediate termination of your
              access to the Platform, or when it is impossible to remedy the breach.
            </Typography>
            <Typography className={baseCSS}>
              Where we disable or delete your account, we will also provide information about this action and, where
              required under applicable data protection law, the reasons for it. Where the decision is based solely on
              automated processing and produces legal or similarly significant effects, you have the right to request
              human intervention, to express your point of view, and to contest the decision. We may limit or withhold
              specific information where providing it would adversely affect the rights and freedoms of others,
              compromise the security or integrity of our services, or where we are legally required to do so.
            </Typography>
            <Typography className={baseCSS}>
              You acknowledge and agree that we shall not be liable to you or any third-party for any termination or
              suspension of your access to the Platform.
            </Typography>
          </Container>
        </Container>
      </DialogContent>
    </Dialog>
  );
}
